import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { handleAPIError } from "@/lib/errorHandler";
import { cn } from "@/lib/utils";
import { Loader2, RefreshCcw, Save, ShieldCheck, Users, LogOut, Plus, Activity, Database, Building2, Clock, Trash2, Key, Ban, HardDrive, Search, Copy, Check, ExternalLink, Shield, Eye, EyeOff, Menu, X, ChevronDown, ChevronUp, Calendar } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const REACTIONS = [
  { value: 5, emoji: "🤩" },
  { value: 4, emoji: "🙂" },
  { value: 3, emoji: "😐" },
  { value: 1, emoji: "🤬" },
];
const RATING_LABEL = { 5: "Excellent", 4: "Good", 3: "Neutral", 1: "Angry" };
const RATING_COLOR = { 5: "#f59e0b",   4: "#16a34a", 3: "#6366f1", 1: "#dc2626" };
const RATING_BG    = { 5: "#fef3c7",   4: "#dcfce7", 3: "#ede9fe", 1: "#fee2e2" };

type TenantUser = {
  id: string;
  email: string;
  username?: string | null;
  fullName?: string | null;
  roles?: string[];
  userId?: string;
  createdAt?: string;
};

type Membership = {
  tenantId: string;
  organizationName: string;
  userId: string;
};

type MasterUser = {
  email: string;
  username?: string | null;
  fullName?: string | null;
  plainPassword?: string | null;
  memberships: Membership[];
};

type FeatureCatalogItem = {
  id: string;
  featureKey: string;
  label: string;
  featureGroup?: string | null;
  description?: string | null;
  defaultEnabled: boolean;
};

type UserFeatureAssignment = FeatureCatalogItem & {
  allowed: boolean;
  isOverride?: boolean;
};

const GROUP_LABELS: Record<string, string> = {
  core: "Core Overview",
  master: "Master Data",
  invoice: "Invoices & Purchases",
  day_business: "Day Business",
  product_stock: "Product Stock",
  reports: "Reports",
  relational: "Relational Modules",
  credit: "Credit Management",
  support: "Support Tools",
  admin: "Administration",
  general: "General",
};

// Sidebar Structure
interface SidebarItem {
  section: string;
  featureKey?: string | null;
  isGroup?: boolean;
  level: number;
  isSuperAdminOnly?: boolean;
}

const SIDEBAR_STRUCTURE: SidebarItem[] = [
  { section: "Dashboard", featureKey: "dashboard", level: 0 },
  { section: "Master", isGroup: true, level: 0 },
  { section: "Organization", featureKey: null, level: 1, isSuperAdminOnly: true },
  { section: "Fuel Products", featureKey: "fuel_products", level: 1 },
  { section: "Lubricants", featureKey: "lubricants", level: 1 },
  { section: "Credit Customer", featureKey: "credit_customers", level: 1 },
  { section: "Employees", featureKey: "employees", level: 1 },
  { section: "Expense Types", featureKey: "expense_types", level: 1 },
  { section: "Busi. Crd/Debit Party", featureKey: "business_parties", level: 1 },
  { section: "Vendor", featureKey: "vendors", level: 1 },
  { section: "Swipe Machines", featureKey: "swipe_machines", level: 1 },
  { section: "Expiry Items", featureKey: "expiry_items", level: 1 },
  { section: "Tank & Nozzel", featureKey: "tank_nozzle", level: 1 },
  { section: "Pump Setting", featureKey: "pump_settings", level: 1 },
  { section: "DutyPay Shift", featureKey: "duty_pay_shift", level: 1 },
  { section: "Print Templates", featureKey: "print_templates", level: 1 },
  { section: "Guest Entry", featureKey: "guest_entry", level: 1 },
  { section: "Denominations", featureKey: "denominations", level: 1 },
  { section: "Invoice", isGroup: true, level: 0 },
  { section: "Liquid Purchase", featureKey: "liquid_purchase", level: 1 },
  { section: "Lubs Purchase", featureKey: "lubs_purchase", level: 1 },
  { section: "Day Business", isGroup: true, level: 0 },
  { section: "Day Assignings", featureKey: "day_assignings", level: 1 },
  { section: "Daily Sale Rate", featureKey: "daily_sale_rate", level: 1 },
  { section: "Sale Entry", featureKey: "sale_entry", level: 1 },
  { section: "Lubricants", featureKey: "lub_sale", level: 1 },
  { section: "Swipe", featureKey: "swipe", level: 1 },
  { section: "Credit Sale", featureKey: "credit_sale", level: 1 },
  { section: "Expenses", featureKey: "expenses", level: 1 },
  { section: "Recovery", featureKey: "recovery", level: 1 },
  { section: "Emp Cash Recovery", featureKey: "employee_cash_recovery", level: 1 },
  { section: "DayOpening Stock", featureKey: "day_opening_stock", level: 1 },
  { section: "Day Settlement", featureKey: "day_settlement", level: 1 },
  { section: "Statement Generation", featureKey: "statement_generation", level: 0 },
  { section: "Product Stock", isGroup: true, level: 0 },
  { section: "Stock Report", featureKey: "stock_report", level: 1 },
  { section: "Lub Loss", featureKey: "lub_loss", level: 1 },
  { section: "Lubs Stock", featureKey: "lubs_stock", level: 1 },
  { section: "Minimum stock", featureKey: "minimum_stock", level: 1 },
  { section: "Shift Sheet Entry", featureKey: "shift_sheet_entry", level: 0 },
  { section: "Busi. Cr/Dr Trxns", featureKey: "business_crdr_transactions", level: 0 },
  { section: "Vendor Transaction", featureKey: "vendor_transactions", level: 0 },
  { section: "Reports", featureKey: "reports", level: 0 },
  { section: "Generate SaleInvoice", featureKey: "generate_sale_invoice", level: 0 },
  { section: "Generated Invoices", featureKey: "generated_invoices", level: 0 },
  { section: "Credit Limit Reports", featureKey: "credit_limit_reports", level: 0 },
  { section: "Relational Features", isGroup: true, level: 0 },
  { section: "Interest Trans", featureKey: "interest_transactions", level: 1 },
  { section: "Sheet Records", featureKey: "sheet_records", level: 1 },
  { section: "Day cash report", featureKey: "day_cash_report", level: 1 },
  { section: "Tanker sale", featureKey: "tanker_sale", level: 1 },
  { section: "Guest Sales", featureKey: "guest_sales", level: 1 },
  { section: "Attendance", featureKey: "attendance", level: 1 },
  { section: "Duty Pay", featureKey: "duty_pay", level: 1 },
  { section: "Sales Officer", featureKey: "sales_officer", level: 1 },
  { section: "Credit Requests", featureKey: "credit_requests", level: 1 },
  { section: "Expiry Items", featureKey: "expiry_items", level: 1 },
  { section: "Feedback", featureKey: "feedback", level: 1 },
];

const fetchUsers = async (): Promise<TenantUser[]> => {
  const { fetchWithTimeout } = await import("@/utils/fetchWithTimeout");
  const response = await fetchWithTimeout("/api/users", { credentials: "include" }, 10000);
  const data = await response.json();
  if (!response.ok) throw { status: response.status, message: data?.error || "Failed to load users" };
  return data?.users ?? [];
};

const fetchMasterUsers = async (): Promise<MasterUser[]> => {
  const { fetchWithTimeout } = await import("@/utils/fetchWithTimeout");
  const response = await fetchWithTimeout("/api/developer-mode/master-users?includeTest=1", { credentials: "include" }, 10000);
  const data = await response.json();
  if (!response.ok || !data?.ok) throw { status: response.status, message: data?.error || "Failed to load users" };
  return data?.users ?? [];
};

const fetchFeatureCatalog = async (): Promise<FeatureCatalogItem[]> => {
  const { fetchWithTimeout } = await import("@/utils/fetchWithTimeout");
  const response = await fetchWithTimeout("/api/developer-mode/features", { credentials: "include" }, 10000);
  const data = await response.json();
  if (!response.ok || !data?.ok) throw { status: response.status, message: data?.error || "Failed to load feature catalog" };
  return data?.features ?? [];
};

const fetchUserFeatures = async (userId: string, tenantId?: string): Promise<UserFeatureAssignment[]> => {
  const { fetchWithTimeout } = await import("@/utils/fetchWithTimeout");
  const url = tenantId
    ? `/api/developer-mode/users/${userId}/features?tenantId=${encodeURIComponent(tenantId)}`
    : `/api/developer-mode/users/${userId}/features`;
  const headers: Record<string, string> = {};
  if (tenantId) headers["X-Tenant-Id"] = tenantId;
  const response = await fetchWithTimeout(url, { credentials: "include", headers }, 10000);
  const data = await response.json();
  if (!response.ok || !data?.ok) throw { status: response.status, message: data?.error || "Failed to load user features" };
  if (data.features && !Array.isArray(data.features)) {
    return Object.entries<Record<string, boolean>>(data.features).map(([featureKey, allowed]) => ({
      featureKey,
      allowed: Boolean(allowed),
      defaultEnabled: false,
    } as unknown as UserFeatureAssignment));
  }
  return data?.features ?? [];
};

type SystemHealth = {
  totalTenants: number;
  totalUsers: number;
  databaseStatus: 'connected' | 'error';
  lastSyncTime: string;
};

type TenantSettingsDraft = {
  organizationName: string;
  superAdminEmail: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
};

const formatDateForInput = (value?: string | null) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const fetchSystemHealth = async (): Promise<SystemHealth> => {
  const { fetchWithTimeout } = await import("@/utils/fetchWithTimeout");
  const response = await fetchWithTimeout("/api/developer-mode/system-health", { credentials: "include" }, 10000);
  const data = await response.json();
  if (!response.ok || !data?.ok) throw { status: response.status, message: data?.error || "Failed to load system health" };
  return {
    totalTenants: data.totalTenants || 0,
    totalUsers: data.totalUsers || 0,
    databaseStatus: data.databaseStatus || 'error',
    lastSyncTime: data.lastSyncTime || new Date().toISOString(),
  };
};

const fetchTenants = async (): Promise<any[]> => {
  const { fetchWithTimeout } = await import("@/utils/fetchWithTimeout");
  const response = await fetchWithTimeout("/api/developer-mode/tenants", { credentials: "include" }, 10000);
  const data = await response.json();
  if (!response.ok || !data?.ok) throw { status: response.status, message: data?.error || "Failed to load tenants" };
  return data?.tenants ?? [];
};

const fetchAuditLogs = async (userEmail?: string): Promise<any[]> => {
  const { fetchWithTimeout } = await import("@/utils/fetchWithTimeout");
  const url = userEmail ? `/api/developer-mode/audit-logs?userEmail=${encodeURIComponent(userEmail)}&limit=50` : `/api/developer-mode/audit-logs?limit=50`;
  const response = await fetchWithTimeout(url, { credentials: "include" }, 10000);
  const data = await response.json();
  if (!response.ok || !data?.ok) throw { status: response.status, message: data?.error || "Failed to load audit logs" };
  return data?.logs ?? [];
};

const resetPassword = async (email: string) => {
  const res = await fetch(`/api/developer-mode/users/${encodeURIComponent(email)}/reset-password`, {
    method: "POST",
    credentials: "include"
  });
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || "Password reset failed");
  return data;
};

export default function DeveloperMode() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser, refreshUser, signOut } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [newOrgForm, setNewOrgForm] = useState({ organizationName: "", superAdminEmail: "", superAdminPassword: "", superAdminFullName: "" });
  const [newUserForm, setNewUserForm] = useState({ email: "", username: "", fullName: "", password: "", role: "manager" as "super_admin" | "manager" | "dsm" });
  
  // Local state for visibility and loading
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});
  const [isRecovering, setIsRecovering] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Local state for editing tenant emails
  const [tenantSettings, setTenantSettings] = useState<Record<string, TenantSettingsDraft>>({});

  const { data: systemHealth } = useQuery({ queryKey: ["developer-mode-system-health"], queryFn: fetchSystemHealth, refetchInterval: 30000 });
  const { data: tenants = [], isLoading: isLoadingTenants, refetch: refetchTenants } = useQuery({ queryKey: ["developer-mode-tenants"], queryFn: fetchTenants });
  const { data: masterUsers = [], refetch: refetchMasterUsers } = useQuery({ queryKey: ["developer-mode-master-users"], queryFn: fetchMasterUsers });

  const [expandedTenantId, setExpandedTenantId] = useState<string | null>(null);

  const { data: devFeedbacks = [], refetch: refetchDevFeedbacks, isLoading: isLoadingFeedbacks } = useQuery({
    queryKey: ["developer-mode-feedbacks"],
    queryFn: async () => {
      const res = await fetch(`/api/feedback?all_tenants=true`);
      const data = await res.json();
      if (!data.success) throw new Error("Failed to load feedbacks");
      return (data.rows || []).map((r: any) => ({
        ...r,
        id: r.id || r._id?.toString()
      }));
    }
  });

  const feedbacksByTenant = useMemo(() => {
    const groups: Record<string, any[]> = {};
    devFeedbacks.forEach((f: any) => {
      const tid = f.tenantId || "default";
      if (!groups[tid]) groups[tid] = [];
      groups[tid].push(f);
    });
    return groups;
  }, [devFeedbacks]);
  const { data: userFeatures = [], isLoading: isLoadingUserFeatures, refetch: refetchUserFeatures } = useQuery({
    queryKey: ["developer-mode-user-features", selectedUserId, selectedTenantId],
    queryFn: () => fetchUserFeatures(selectedUserId as string, selectedTenantId as string),
    enabled: Boolean(selectedUserId && selectedTenantId),
  });

  useEffect(() => {
    if (userFeatures) {
      const next: Record<string, boolean> = {};
      userFeatures.forEach(f => { next[f.featureKey] = f.allowed; });
      setBaseline(next);
      setDraft({ ...next });
    }
  }, [userFeatures]);

  useEffect(() => {
    setTenantSettings(prev => {
      const next = { ...prev };
      tenants.forEach((tenant: any) => {
        if (!next[tenant.id]) {
          // First time seeing this tenant — initialize all fields
          next[tenant.id] = {
            organizationName: tenant.organizationName || "",
            superAdminEmail: tenant.superAdminEmail || "",
            subscriptionStartDate: formatDateForInput(tenant.subscriptionStartDate),
            subscriptionEndDate: formatDateForInput(tenant.subscriptionEndDate),
          };
        } else {
          // Always sync subscription dates from server (they can only be changed via save)
          next[tenant.id] = {
            ...next[tenant.id],
            subscriptionStartDate: formatDateForInput(tenant.subscriptionStartDate),
            subscriptionEndDate: formatDateForInput(tenant.subscriptionEndDate),
          };
        }
      });
      return next;
    });
  }, [tenants]);


  const mutation = useMutation({
    mutationFn: async ({ userId, features }: { userId: string; features: any[] }) => {
      const url = selectedTenantId ? `/api/developer-mode/users/${userId}/features?tenantId=${encodeURIComponent(selectedTenantId)}` : `/api/developer-mode/users/${userId}/features`;
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ features }),
        credentials: "include"
      });
      if (!res.ok) throw new Error("Failed to save permissions");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "System Synchronized", description: "Permissions updated across network lattice." });
      queryClient.invalidateQueries({ queryKey: ["developer-mode-user-features"] });
      queryClient.invalidateQueries({ queryKey: ["feature-access"] });
    }
  });

  const addUserMutation = useMutation({
    mutationFn: async (userData: any) => {
      const res = await fetch(`/api/developer-mode/tenants`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
        credentials: "include"
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Provisioning failed");
      return data;
    },
    onSuccess: () => {
      toast({ title: "Node Initialized", description: "New organization provisioned successfully." });
      refetchTenants();
      refetchMasterUsers();
      setNewOrgForm({ organizationName: "", superAdminEmail: "", superAdminPassword: "", superAdminFullName: "" });
    },
    onError: (err: any) => {
      toast({ title: "Provisioning Aborted", description: err.message, variant: "destructive" });
    }
  });

  const saveTenantSettingsMutation = useMutation({
    mutationFn: async ({ tenantId, settings, type }: { tenantId: string; settings: TenantSettingsDraft; type: "general" | "subscription" }) => {
      const payload = type === "general"
        ? { organizationName: settings.organizationName, superAdminEmail: settings.superAdminEmail }
        : { subscriptionStartDate: settings.subscriptionStartDate, subscriptionEndDate: settings.subscriptionEndDate };

      const res = await fetch(`/api/developer-mode/tenants/${tenantId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.error || "Failed to save pump settings");
      return data;
    },
    onSuccess: (data, variables) => {
      const title = variables.type === "general" ? "General Details Saved" : "Subscription Range Confirmed";
      toast({ title, description: "Developer-controlled tenant settings updated." });
      refetchTenants();
    },
    onError: (err: any) => {
      toast({ title: "Save Failed", description: err.message, variant: "destructive" });
    }
  });

  const deleteTenantMutation = useMutation({
    mutationFn: async (tenantId: string) => {
      if (!window.confirm("Are you sure you want to delete this pump and all associated data?")) return;
      const res = await fetch(`/api/developer-mode/tenants/${tenantId}`, {
        method: "DELETE",
        credentials: "include"
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to delete pump");
      return data;
    },
    onSuccess: () => {
      toast({ title: "Pump Deleted", description: "The pump network has been decommissioned." });
      refetchTenants();
      refetchMasterUsers();
      // Invalidate health queries too as counts changed
      queryClient.invalidateQueries({ queryKey: ["developer-mode-system-health"] });
    },
    onError: (err: any) => {
      toast({ 
        title: "Decommissioning Failed", 
        description: err.message || "The network link could not be severed.", 
        variant: "destructive" 
      });
    }
  });

  const addOperatorMutation = useMutation({
    mutationFn: async ({ tenantId, userData }: { tenantId: string; userData: any }) => {
      const res = await fetch(`/api/developer-mode/tenants/${tenantId}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
        credentials: "include"
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to create identity");
      return data;
    },
    onSuccess: () => {
      toast({ title: "Identity Created", description: "Operator added to the pump network." });
      refetchMasterUsers();
      setNewUserForm({ email: "", username: "", fullName: "", password: "", role: "manager" });
    },
    onError: (err: any) => {
      toast({ title: "Identity Error", description: err.message, variant: "destructive" });
    }
  });

  const revokeMutation = useMutation({
    mutationFn: async ({ tenantId, email }: { tenantId: string; email: string }) => {
      const res = await fetch(`/api/developer-mode/tenant-users/map`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId, email }),
        credentials: "include"
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Revocation failed");
      return data;
    },
    onSuccess: () => {
      toast({ title: "Access Revoked", description: "Identity mapping removed from the network." });
      refetchMasterUsers();
    },
    onError: (err: any) => {
      toast({ title: "Revocation Failed", description: err.message, variant: "destructive" });
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: resetPassword,
    onMutate: () => setIsRecovering(true),
    onSuccess: async (data) => {
      // Cancel any outgoing refetches so they don't race
      await queryClient.cancelQueries({ queryKey: ["developer-mode-master-users"] });

      toast({ 
        title: "Security Logic Reset", 
        description: `Password for ${data.userEmail} has been reset. New key: ${data.temporaryPassword}`,
        duration: 10000 
      });

      // Inject the new password into the query cache so future refetches start from fresh state
      queryClient.setQueryData(["developer-mode-master-users"], (oldData: MasterUser[] | undefined) => {
        if (!Array.isArray(oldData)) return oldData;
        return oldData.map((u) =>
          u.email.toLowerCase() === data.userEmail.toLowerCase()
            ? { ...u, plainPassword: data.temporaryPassword }
            : u
        );
      });

      // DO NOT refetch - the pinned local state in TenantUserTable handles display
    },
    onError: (err: any) => {
      toast({ title: "Reset Failed", description: err.message, variant: "destructive" });
    },
    onSettled: () => setIsRecovering(false),
  });

  const deleteFeedbackMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!window.confirm("Are you sure you want to delete this feedback?")) return;
      const res = await fetch(`/api/feedback/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || "Failed to delete feedback");
      return data;
    },
    onSuccess: () => {
      toast({ title: "Feedback Cleared", description: "The response was successfully deleted." });
      refetchDevFeedbacks();
    },
    onError: (err: any) => {
      toast({ title: "Deletion Failed", description: err.message, variant: "destructive" });
    }
  });

  const clearAllFeedbackMutation = useMutation({
    mutationFn: async () => {
      const scope = showAllFeedbackTenants ? "ALL pumps" : "the current pump";
      if (!window.confirm(`WARNING: Are you sure you want to clear all feedback for ${scope}? This cannot be undone.`)) return;

      const res = await fetch("/api/feedback/clear-all", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ all_tenants: showAllFeedbackTenants }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error("Failed to clear feedback");
      return data;
    },
    onSuccess: () => {
      toast({ title: "Feedback Purged", description: "Selected feedback responses were cleared." });
      refetchDevFeedbacks();
    },
    onError: (err: any) => {
      toast({ title: "Purge Failed", description: err.message, variant: "destructive" });
    }
  });

  const revealOrRecoverPassword = (user: MasterUser) => {
    if (user.plainPassword) {
      setVisiblePasswords(prev => ({ ...prev, [user.email.toLowerCase()]: !prev[user.email.toLowerCase()] }));
      return;
    }
    toast({ 
      title: "Sync Required", 
      description: "This legacy account needs a fresh key. Click the Refresh (🔄) icon.", 
      variant: "destructive" 
    });
  };

  const handleCreateOperator = () => {
    if (!selectedTenantId) {
      toast({ title: "Target Missing", description: "Please select a pump network first.", variant: "destructive" });
      return;
    }
    if (!newUserForm.username || !newUserForm.password) {
      toast({ title: "Incomplete Form", description: "Username and password are required.", variant: "destructive" });
      return;
    }

    const tenant = tenants.find((t: any) => t.id === selectedTenantId);
    const orgPrefix = tenant?.organizationName?.replace(/\s+/g, '').toLowerCase() || 'network';
    const email = newUserForm.username.includes('@') ? newUserForm.username : `${newUserForm.username}@${orgPrefix}.com`;

    addOperatorMutation.mutate({
      tenantId: selectedTenantId,
      userData: {
        ...newUserForm,
        email,
        fullName: newUserForm.username,
        role: "manager"
      }
    });
  };

  const handleToggleFeature = (key: string, val: boolean) => {
    setDraft(prev => ({ ...prev, [key]: val }));
  };

  const handleToggleGroup = (groupName: string, val: boolean) => {
    setDraft(prev => {
      const updated = { ...prev };
      const groupIndex = SIDEBAR_STRUCTURE.findIndex(i => i.section === groupName && i.isGroup);
      if (groupIndex !== -1) {
        for (let i = groupIndex + 1; i < SIDEBAR_STRUCTURE.length; i++) {
          const item = SIDEBAR_STRUCTURE[i];
          if (item.level === 1 && item.featureKey) {
            updated[item.featureKey] = val;
          } else if (item.level === 0 || item.isGroup) break;
        }
      }
      return updated;
    });
  };

  const handleSave = () => {
    if (!selectedUserId) return;
    const payload = userFeatures.map(f => ({
      featureKey: f.featureKey,
      allowed: draft[f.featureKey] ?? f.allowed
    }));
    mutation.mutate({ userId: selectedUserId, features: payload });
  };

  const groupedFeatures = useMemo(() => {
    const result: Record<string, any[]> = {};
    let currentGroup = "General";
    SIDEBAR_STRUCTURE.forEach(item => {
      if (item.isGroup) currentGroup = item.section;
      else if (item.featureKey) {
        const feat = userFeatures.find(f => f.featureKey === item.featureKey);
        if (feat) {
          if (!result[currentGroup]) result[currentGroup] = [];
          result[currentGroup].push(feat);
        }
      }
    });
    return result;
  }, [userFeatures]);

  const getAccessCode = (id: string) => {
    const parts = id.split('-');
    return (parts[parts.length - 1] || id).slice(-6).toUpperCase();
  };

  const getSuperAdminEmail = (id: string) => {
    const admin = masterUsers.find((u: any) => 
      String(u.tenantId) === String(id) && (u.role === 'super_admin' || u.role === 'ADMIN') ||
      u.memberships?.some((m: any) => String(m.tenantId) === String(id) && (m.role === 'super_admin' || m.role === 'ADMIN'))
    );
    return admin?.email || "";
  };

  const updateTenantSetting = (tenantId: string, key: keyof TenantSettingsDraft, value: string) => {
    setTenantSettings(prev => ({
      ...prev,
      [tenantId]: {
        organizationName: prev[tenantId]?.organizationName || "",
        superAdminEmail: prev[tenantId]?.superAdminEmail || "",
        subscriptionStartDate: prev[tenantId]?.subscriptionStartDate || "",
        subscriptionEndDate: prev[tenantId]?.subscriptionEndDate || "",
        [key]: value,
      },
    }));
  };

  const getDraftSubscriptionStatus = (start?: string | null, end?: string | null) => {
    if (!start && !end) {
      return { label: "Always Active (No limits)", color: "text-emerald-600 bg-emerald-50 border-emerald-100" };
    }
    const d = new Date();
    const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    
    const startStr = start ? start.slice(0, 10) : "";
    const endStr = end ? end.slice(0, 10) : "";
    
    if (startStr && startStr > todayStr) {
      const todayVal = new Date(todayStr).getTime();
      const startVal = new Date(startStr).getTime();
      const diff = startVal - todayVal;
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
      return { label: `Starts in ${days} day${days !== 1 ? 's' : ''}`, color: "text-amber-600 bg-amber-50 border-amber-100" };
    }
    
    if (endStr && endStr < todayStr) {
      const todayVal = new Date(todayStr).getTime();
      const endVal = new Date(endStr).getTime();
      const diff = todayVal - endVal;
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
      return { label: `Expired ${days} day${days !== 1 ? 's' : ''} ago`, color: "text-rose-600 bg-rose-50 border-rose-100" };
    }
    
    return { label: "Active", color: "text-emerald-600 bg-emerald-50 border-emerald-100" };
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col font-sans text-slate-900">
      {/* Header */}
      <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-50 shadow-sm">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-indigo-600 shrink-0" />
          <h1 className="text-sm font-black uppercase tracking-tighter text-slate-800 hidden sm:block">Developer Master Control</h1>
          <h1 className="text-xs font-black uppercase tracking-tighter text-slate-800 sm:hidden">Dev Control</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 border-slate-200 text-slate-600 hover:bg-slate-50 lg:hidden"
            onClick={() => setSidebarOpen(prev => !prev)}
          >
            <Menu className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="h-8 border-slate-200 text-slate-600 hover:bg-slate-50 gap-2 font-bold text-[10px] uppercase shadow-sm"
            onClick={() => signOut()}
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Secure Logout</span>
          </Button>
        </div>
      </header>

      <div className="flex flex-1 relative">
        {/* Mobile overlay backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Left Sidebar */}
        <aside className={`
          fixed lg:sticky top-14 lg:top-0 z-50 lg:z-auto
          w-[300px] lg:w-[320px]
          bg-white border-r border-slate-200
          p-6 overflow-y-auto shrink-0 flex flex-col gap-8
          h-[calc(100vh-3.5rem)] lg:h-[calc(100vh-3.5rem)]
          transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          {/* Close button - mobile only */}
          <button
            className="lg:hidden self-end -mt-2 -mr-2 p-1 rounded-lg text-slate-400 hover:text-slate-600"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Provisioning Section */}
          <section className="space-y-4">
            <h2 className="font-black uppercase tracking-tight text-slate-400 text-[10px]">Provision New Client</h2>
            <div className="flex flex-col gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-500 uppercase">Company / Pump Name</Label>
                <Input className="h-9 bg-slate-50/50 border-slate-200 text-sm focus:bg-white" placeholder="e.g. Royal Petroleum" value={newOrgForm.organizationName} onChange={e => setNewOrgForm({...newOrgForm, organizationName: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-500 uppercase">Owner Name</Label>
                <Input className="h-9 bg-slate-50/50 border-slate-200 text-sm focus:bg-white" placeholder="e.g. Ram Singh" value={newOrgForm.superAdminFullName} onChange={e => setNewOrgForm({...newOrgForm, superAdminFullName: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-500 uppercase">Report Email (Verified)</Label>
                <Input className="h-9 bg-slate-50/50 border-slate-200 text-sm focus:bg-white" placeholder="reports@yourdomain.com" value={newOrgForm.superAdminEmail} onChange={e => setNewOrgForm({...newOrgForm, superAdminEmail: e.target.value})} />
              </div>
              <Button 
                className="w-full bg-slate-900 hover:bg-black text-white font-black text-[10px] h-9 uppercase tracking-wider mt-2 shadow-sm transition-all" 
                onClick={() => {
                  if (!newOrgForm.organizationName || !newOrgForm.superAdminEmail) {
                    toast({ title: "Incomplete Form", description: "Company Name and Report Email are required.", variant: "destructive" });
                    return;
                  }
                  const ownerName = newOrgForm.superAdminFullName?.trim();
                  const baseOrg = newOrgForm.organizationName.trim().replace(/\s+/g, '_').toLowerCase();
                  
                  // Use owner name as primary username source (if provided), or fall back to org_admin
                  // We remove spaces and lower-case it to make a clean login handle
                  const username = ownerName 
                    ? ownerName.replace(/\s+/g, '').toLowerCase() 
                    : `${baseOrg}_admin`;

                  const randomPassword = Math.random().toString(36).slice(-8) + "Az1!";
                  
                  addUserMutation.mutate({
                    ...newOrgForm,
                    superAdminUsername: username,
                    superAdminPassword: randomPassword
                  });
                }}
                disabled={addUserMutation.isPending}
              >
                {addUserMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : <Plus className="h-3 w-3 mr-2" />}
                Generate Referral Sub-system
              </Button>
            </div>
          </section>

          <div className="h-px bg-slate-100" />

          {/* Add Operator Section */}
          <section className="space-y-4">
            <h2 className="font-black uppercase tracking-tight text-slate-400 text-[10px]">Add Operator to Pump</h2>
            <div className="flex flex-col gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-500 uppercase">Select Pump Network</Label>
                <Select value={selectedTenantId || ''} onValueChange={v => setSelectedTenantId(v)}>
                  <SelectTrigger className="h-9 bg-slate-50/50 border-slate-200 text-sm focus:bg-white"><SelectValue placeholder="Choose a pump..." /></SelectTrigger>
                  <SelectContent>{tenants.map((t:any) => <SelectItem key={t.id} value={t.id}>{t.organizationName}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-500 uppercase">Operator Username</Label>
                <Input className="h-9 bg-slate-50/50 border-slate-200 text-sm focus:bg-white" value={newUserForm.username} onChange={e => setNewUserForm({...newUserForm, username: e.target.value})} placeholder="e.g. operator_name" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-500 uppercase">Password</Label>
                <Input type="password" className="h-9 bg-slate-50/50 border-slate-200 text-sm focus:bg-white" value={newUserForm.password} onChange={e => setNewUserForm({...newUserForm, password: e.target.value})} placeholder="••••••••" />
              </div>
              <Button 
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-black text-[10px] h-9 uppercase tracking-wider mt-2 shadow-sm transition-all"
                onClick={handleCreateOperator}
                disabled={addOperatorMutation.isPending}
              >
                {addOperatorMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : <ShieldCheck className="h-3 w-3 mr-2" />}
                Create Access Identity
              </Button>
            </div>
          </section>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto bg-slate-50/50 min-w-0">
          <Tabs defaultValue="tenants" className="w-full space-y-6">
            <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <TabsList className="bg-slate-100 p-1">
                <TabsTrigger value="tenants" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs font-semibold">🏢 Tenant Bunks</TabsTrigger>
                <TabsTrigger value="feedback" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs font-semibold">💬 Customer Feedback</TabsTrigger>
                <TabsTrigger value="templates" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs font-semibold">🖨️ Print Templates Builder</TabsTrigger>
              </TabsList>
              <div className="text-xs text-slate-500 font-medium hidden sm:block">
                Network Status: <span className="text-emerald-500 font-bold capitalize">{systemHealth?.databaseStatus || "connected"}</span>
              </div>
            </div>

            <TabsContent value="tenants" className="max-w-4xl mx-auto space-y-6 sm:space-y-10 focus:outline-none">
              {tenants.map((tenant: any) => {
                const settings = tenantSettings[tenant.id] || {
                  organizationName: tenant.organizationName || "",
                  superAdminEmail: tenant.superAdminEmail || tenant.super_admin_email || getSuperAdminEmail(tenant.id) || "",
                  subscriptionStartDate: formatDateForInput(tenant.subscriptionStartDate),
                  subscriptionEndDate: formatDateForInput(tenant.subscriptionEndDate),
                };
                const isSaving = saveTenantSettingsMutation.isPending && saveTenantSettingsMutation.variables?.tenantId === tenant.id;
                const isSavingGeneral = isSaving && saveTenantSettingsMutation.variables?.type === "general";
                const isSavingSubscription = isSaving && saveTenantSettingsMutation.variables?.type === "subscription";

                return (
                <div key={tenant.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6 sm:mb-10">
                  {/* Pump Card Header */}
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="text-lg sm:text-xl font-black text-[#1e293b] leading-tight">{tenant.organizationName ? tenant.organizationName.toLowerCase() : 'organization'}</h3>
                        <p className="text-xs text-slate-500 uppercase mt-1">OWNER: {tenant.superAdminFullName || 'ADMIN'}</p>
                      </div>

                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-bold text-[#94a3b8] uppercase tracking-wide hidden sm:block">ACCESS CODE</span>
                          <div 
                            className="h-9 sm:h-10 flex items-center px-3 sm:px-4 bg-[#f1f5f9] rounded-lg cursor-pointer hover:bg-[#e2e8f0] transition-colors"
                            onClick={() => {
                              navigator.clipboard.writeText(getAccessCode(tenant.id));
                              toast({ title: "Copied!", description: "Access code copied to clipboard" });
                            }}
                            title="Click to copy access code"
                          >
                              <span className="font-bold text-[#1e293b] text-[13px] sm:text-[15px]">{getAccessCode(tenant.id)}</span>
                              <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#64748b] ml-2 sm:ml-3" />
                          </div>
                        </div>
                        
                        <Button 
                          variant="outline" 
                          className="text-[#ef4444] border-[#fecaca] hover:bg-[#fef2f2] h-9 sm:h-10 font-bold text-xs sm:text-sm px-3 sm:px-4 gap-1.5 sm:gap-2 rounded-xl"
                          onClick={() => deleteTenantMutation.mutate(tenant.id)}
                          disabled={deleteTenantMutation.isPending && deleteTenantMutation.variables === tenant.id}
                        >
                          {deleteTenantMutation.isPending && deleteTenantMutation.variables === tenant.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                          Delete Pump
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mt-5">
                      
                      {/* Left Side: General Settings */}
                      <div className="bg-slate-50/50 rounded-xl p-4 border border-slate-200/60 flex flex-col justify-between space-y-4">
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-blue-50 text-blue-600 rounded-md">
                              <Building2 className="h-4 w-4" />
                            </div>
                            <span className="text-[11px] font-black uppercase text-slate-700 tracking-wider">General Settings</span>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="space-y-1">
                              <Label className="text-[10px] font-bold text-slate-500 uppercase">Pump Name</Label>
                              <Input
                                className="h-9 bg-white border-[#e2e8f0] text-[13px] font-medium rounded-md focus-visible:ring-1 focus-visible:ring-blue-500 disabled:bg-slate-100 disabled:text-slate-400"
                                value={settings.organizationName}
                                onChange={(e) => updateTenantSetting(tenant.id, "organizationName", e.target.value)}
                                placeholder="Pump/company name"
                                disabled={isSaving}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] font-bold text-slate-500 uppercase">Admin Email</Label>
                              <Input
                                className="h-9 bg-white border-[#e2e8f0] text-[13px] font-medium rounded-md focus-visible:ring-1 focus-visible:ring-blue-500 disabled:bg-slate-100 disabled:text-slate-400"
                                value={settings.superAdminEmail}
                                onChange={(e) => updateTenantSetting(tenant.id, "superAdminEmail", e.target.value)}
                                placeholder="Enter verified report email"
                                disabled={isSaving}
                              />
                            </div>
                          </div>
                        </div>

                        <Button
                          className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white h-9 font-bold text-xs rounded-lg uppercase tracking-wider mt-4"
                          onClick={() => {
                            if (isSaving) return;
                            saveTenantSettingsMutation.mutate({ tenantId: tenant.id, settings, type: "general" });
                          }}
                          disabled={isSavingGeneral}
                        >
                          {isSavingGeneral ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                          ) : (
                            <Save className="h-3.5 w-3.5 mr-2" />
                          )}
                          Save General Details
                        </Button>
                      </div>

                      {/* Right Side: Subscription Settings */}
                      <div className="bg-slate-50/50 rounded-xl p-4 border border-slate-200/60 flex flex-col justify-between space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 bg-violet-50 text-violet-600 rounded-md">
                                <Calendar className="h-4 w-4" />
                              </div>
                              <span className="text-[11px] font-black uppercase text-slate-700 tracking-wider">Subscription Control</span>
                            </div>
                            {/* Live Badge Status */}
                            {(() => {
                              const status = getDraftSubscriptionStatus(settings.subscriptionStartDate, settings.subscriptionEndDate);
                              return (
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${status.color}`}>
                                  {status.label}
                                </span>
                              );
                            })()}
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-[10px] font-bold text-slate-500 uppercase">Subscription Start</Label>
                              <Input
                                type="date"
                                className="h-9 bg-white border-[#e2e8f0] text-[13px] font-medium rounded-md focus-visible:ring-1 focus-visible:ring-violet-500 disabled:bg-slate-100 disabled:text-slate-400"
                                value={settings.subscriptionStartDate}
                                onChange={(e) => updateTenantSetting(tenant.id, "subscriptionStartDate", e.target.value)}
                                disabled={isSaving}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] font-bold text-slate-500 uppercase">Subscription End</Label>
                              <Input
                                type="date"
                                className="h-9 bg-white border-[#e2e8f0] text-[13px] font-medium rounded-md focus-visible:ring-1 focus-visible:ring-violet-500 disabled:bg-slate-100 disabled:text-slate-400"
                                value={settings.subscriptionEndDate}
                                onChange={(e) => updateTenantSetting(tenant.id, "subscriptionEndDate", e.target.value)}
                                disabled={isSaving}
                              />
                            </div>
                          </div>

                          {/* Quick presets row */}
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            <button
                              type="button"
                              onClick={() => {
                                const todayStr = new Date().toLocaleDateString("en-CA");
                                updateTenantSetting(tenant.id, "subscriptionStartDate", todayStr);
                              }}
                              disabled={isSaving}
                              className="text-[9px] font-black uppercase px-2 py-1 bg-white border border-slate-200 text-slate-600 rounded hover:bg-slate-50 hover:text-slate-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Set Start Today
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const today = new Date();
                                const todayStr = today.toLocaleDateString("en-CA");
                                const nextYear = new Date(today.setFullYear(today.getFullYear() + 1)).toLocaleDateString("en-CA");
                                updateTenantSetting(tenant.id, "subscriptionStartDate", todayStr);
                                updateTenantSetting(tenant.id, "subscriptionEndDate", nextYear);
                              }}
                              disabled={isSaving}
                              className="text-[9px] font-black uppercase px-2 py-1 bg-white border border-slate-200 text-slate-600 rounded hover:bg-slate-50 hover:text-slate-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Set Active (1 Year)
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                updateTenantSetting(tenant.id, "subscriptionStartDate", "");
                                updateTenantSetting(tenant.id, "subscriptionEndDate", "");
                              }}
                              disabled={isSaving}
                              className="text-[9px] font-black uppercase px-2 py-1 bg-white border border-slate-200 text-red-600 rounded hover:bg-red-50 hover:border-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Clear Dates
                            </button>
                          </div>
                        </div>

                        <Button
                          className="w-full bg-[#5a32fa] hover:bg-[#4c27d8] text-white h-9 font-bold text-xs rounded-lg uppercase tracking-wider mt-4"
                          onClick={() => {
                            if (isSaving) return;
                            saveTenantSettingsMutation.mutate({ tenantId: tenant.id, settings, type: "subscription" });
                          }}
                          disabled={isSavingSubscription}
                        >
                          {isSavingSubscription ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                          ) : (
                            <Check className="h-3.5 w-3.5 mr-2" />
                          )}
                          Confirm Subscription Range
                        </Button>
                      </div>

                    </div>
                  </div>

                  {/* User Table */}
                  <div className="overflow-x-auto border-t border-slate-200">
                    <table className="w-full text-left bg-white min-w-[480px]">
                      <thead className="bg-[#ffffff] text-xs font-bold uppercase text-[#64748b] border-b border-slate-200">
                        <tr>
                          <th className="px-3 sm:px-6 py-3 sm:py-4">USER IDENTITY</th>
                          <th className="px-3 sm:px-6 py-3 sm:py-4 hidden sm:table-cell">ROLE</th>
                          <th className="px-3 sm:px-6 py-3 sm:py-4">PASSWORD</th>
                          <th className="px-3 sm:px-6 py-3 sm:py-4 text-right">ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <TenantUserTable 
                          tenantId={tenant.id} 
                          masterUsers={masterUsers} 
                          onResetPassword={(email: string) => resetPasswordMutation.mutateAsync(email)}
                          onRevoke={(email: string) => revokeMutation.mutate({ tenantId: tenant.id, email })}
                        />
                      </tbody>
                    </table>
                  </div>
                </div>
                );
              })}
            </TabsContent>

            <TabsContent value="feedback" className="max-w-6xl mx-auto space-y-6 focus:outline-none">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 bg-[#0f172a] text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-800">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💬</span>
                    <div>
                      <h3 className="text-lg font-black text-slate-100 uppercase tracking-tight">Customer Feedbacks Matrix</h3>
                      <p className="text-xs text-slate-400 mt-0.5">Click on any pump name below to expand and view its feedback submissions</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <Button
                      variant="outline"
                      className="text-[#f87171] border-[#ef4444]/30 hover:bg-[#ef4444]/10 bg-transparent h-9 font-bold text-xs uppercase tracking-wider rounded-lg px-4 border"
                      onClick={() => clearAllFeedbackMutation.mutate()}
                      disabled={clearAllFeedbackMutation.isPending}
                    >
                      {clearAllFeedbackMutation.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Purge All Feedbacks
                    </Button>
                  </div>
                </div>

                {isLoadingFeedbacks ? (
                  <div className="p-16 text-center text-slate-400 flex flex-col items-center justify-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-300" />
                    <span className="text-xs font-bold uppercase tracking-wider">Synchronizing feedbacks...</span>
                  </div>
                ) : devFeedbacks.length === 0 ? (
                  <div className="p-16 text-center text-slate-400">
                    <span className="text-4xl block mb-2">📭</span>
                    <span className="text-xs font-bold uppercase tracking-wider">No feedbacks registered yet.</span>
                  </div>
                ) : (
                  <div className="p-6 space-y-4 bg-slate-50/50">
                    {tenants.map((tenant: any) => {
                      const tenantIdStr = tenant.id || tenant._id?.toString();
                      const tenantFeedbacks = feedbacksByTenant[tenantIdStr] || [];
                      const isExpanded = expandedTenantId === tenantIdStr;

                      // Calculate breakdown stats for this specific pump
                      const counts = REACTIONS.reduce((acc: any, r) => {
                        acc[r.value] = tenantFeedbacks.filter(f => f.rating === r.value).length;
                        return acc;
                      }, {});

                      return (
                        <div key={tenantIdStr} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden transition-all duration-200 hover:border-slate-300">
                          {/* Accordion Header */}
                          <button
                            type="button"
                            onClick={() => setExpandedTenantId(isExpanded ? null : tenantIdStr)}
                            className="w-full p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-left hover:bg-slate-50/50 transition-colors focus:outline-none"
                          >
                            <div className="flex items-start sm:items-center gap-3.5">
                              <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-600 border border-indigo-100 flex items-center justify-center">
                                <Building2 className="h-5 w-5" />
                              </div>
                              <div>
                                <h4 className="font-extrabold text-slate-800 text-[15px] uppercase tracking-tight flex items-center gap-2">
                                  {tenant.organizationName || "Unnamed Bunk"}
                                </h4>
                                <span className="text-slate-400 font-bold text-[10px] tracking-wider uppercase block mt-0.5">
                                  Owner Email: <span className="text-slate-600 font-mono font-bold lowercase">{tenant.superAdminEmail || "—"}</span>
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 flex-wrap">
                              {/* Rating Summary Breakdown */}
                              {tenantFeedbacks.length > 0 && (
                                <div className="hidden md:flex items-center gap-2.5 bg-slate-50 border border-slate-100 px-3 py-1 rounded-full text-xs font-semibold text-slate-600">
                                  {REACTIONS.map(r => (
                                    <span key={r.value} className="flex items-center gap-0.5">
                                      <span>{r.emoji}</span>
                                      <span className="font-bold text-slate-700">{counts[r.value] || 0}</span>
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Feedbacks Count Badge */}
                              <span className={cn(
                                "inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider border",
                                tenantFeedbacks.length > 0 
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-100" 
                                  : "bg-slate-100 text-slate-400 border-slate-200"
                              )}>
                                {tenantFeedbacks.length} response{tenantFeedbacks.length !== 1 ? 's' : ''}
                              </span>

                              {/* Toggle Arrow */}
                              {isExpanded ? (
                                <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" />
                              ) : (
                                <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />
                              )}
                            </div>
                          </button>

                          {/* Accordion Collapsible Body */}
                          {isExpanded && (
                            <div className="border-t border-slate-100 bg-slate-50/30">
                              {tenantFeedbacks.length === 0 ? (
                                <div className="p-10 text-center text-slate-400">
                                  <span className="text-2xl block mb-1">💬</span>
                                  <span className="text-xs font-bold uppercase tracking-wider">No feedbacks registered for this bunk pump yet.</span>
                                </div>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-left bg-white text-slate-700">
                                    <thead className="bg-[#f8fafc] text-[10px] font-black uppercase text-[#64748b] border-b border-slate-200">
                                      <tr>
                                        <th className="px-6 py-3.5">Mobile Number</th>
                                        <th className="px-6 py-3.5">Emoji Rating</th>
                                        <th className="px-6 py-3.5">Response Message</th>
                                        <th className="px-6 py-3.5">Submission Timestamp</th>
                                        <th className="px-6 py-3.5 text-right">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 font-medium">
                                      {tenantFeedbacks.map((f: any) => {
                                        const ratingLabel = RATING_LABEL[f.rating] || f.rating;
                                        const ratingColor = RATING_COLOR[f.rating] || "#475569";
                                        const ratingBg = RATING_BG[f.rating] || "#f1f5f9";
                                        const emoji = REACTIONS.find(r => r.value === f.rating)?.emoji || "⭐";

                                        return (
                                          <tr key={f.id} className="hover:bg-slate-50/30 transition-colors">
                                            <td className="px-6 py-4 font-mono font-bold text-slate-800 text-sm">
                                              {f.mobileNumber || "—"}
                                            </td>
                                            <td className="px-6 py-4">
                                              <span
                                                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border"
                                                style={{ background: ratingBg, color: ratingColor, borderColor: `${ratingColor}20` }}
                                              >
                                                <span>{emoji}</span>
                                                <span>{ratingLabel}</span>
                                              </span>
                                            </td>
                                            <td className="px-6 py-4 text-slate-600 font-medium text-[13px]">
                                              {f.message || "User Feedback"}
                                            </td>
                                            <td className="px-6 py-4 text-xs text-slate-500 font-bold">
                                              {new Date(f.createdAt || f.feedback_date).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 font-black text-[10px] uppercase tracking-wider rounded-md px-3 border border-transparent hover:border-red-100"
                                                onClick={() => deleteFeedbackMutation.mutate(f.id)}
                                                disabled={deleteFeedbackMutation.isPending && deleteFeedbackMutation.variables === f.id}
                                              >
                                                {deleteFeedbackMutation.isPending && deleteFeedbackMutation.variables === f.id ? (
                                                  <Loader2 className="h-3 w-3 animate-spin" />
                                                ) : (
                                                  "Delete"
                                                )}
                                              </Button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="templates" className="focus:outline-none">
              <PrintTemplateBuilder tenants={tenants} toast={toast} />
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* Feature Configuration Modal (Existing but updated styles) */}
      <Dialog open={showFeatureModal} onOpenChange={setShowFeatureModal}>
        <DialogContent className="max-w-4xl bg-white p-0 overflow-hidden flex flex-col h-[70vh] border-none shadow-2xl rounded-2xl">
          <DialogHeader className="p-6 bg-slate-900 text-white rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <ShieldCheck className="h-6 w-6 text-indigo-400" />
                <div>
                  <DialogTitle className="text-lg font-black uppercase tracking-tight">Access Control Matrix</DialogTitle>
                  <DialogDescription className="text-slate-400 uppercase text-[9px] font-bold tracking-widest mt-0.5">Configuring: <span className="text-white italic">{selectedEmail}</span></DialogDescription>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" className="text-slate-400 hover:text-white uppercase font-bold text-[10px]" onClick={() => setShowFeatureModal(false)}>Cancel</Button>
                <Button className="bg-white hover:bg-slate-100 text-slate-900 font-black text-[10px] uppercase px-6 h-9 rounded-lg" onClick={() => { handleSave(); setShowFeatureModal(false); }}>Update Identity</Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-8 bg-slate-50/50">
            <div className="grid grid-cols-2 gap-6">
              {Object.entries(groupedFeatures).map(([group, feats]) => (
                <div key={group} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden h-fit">
                  <div className="py-3 px-5 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
                    <h4 className="font-black text-[9px] tracking-widest uppercase text-slate-500">{group}</h4>
                    <span className="text-[9px] font-bold py-0.5 px-2 bg-slate-100 rounded-full text-slate-500">{feats.filter(f => draft[f.featureKey]).length}/{feats.length}</span>
                  </div>
                  <div className="p-3 grid grid-cols-1 gap-1">
                    {feats.map(f => (
                      <div key={f.featureKey} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 group cursor-pointer" onClick={() => handleToggleFeature(f.featureKey, !(draft[f.featureKey] ?? false))}>
                        <span className={`text-[11px] font-bold uppercase tracking-tight transition-colors ${draft[f.featureKey] ? 'text-indigo-600' : 'text-slate-400'}`}>{f.label}</span>
                        <Checkbox checked={draft[f.featureKey] ?? false} onCheckedChange={checked => handleToggleFeature(f.featureKey, !!checked)} className="h-4 w-4 rounded-sm data-[state=checked]:bg-indigo-600 border-slate-300 pointer-events-none" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TenantUserTable({ tenantId, masterUsers, onResetPassword, onRevoke }: any) {
  const { toast } = useToast();
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});
  const [recoveringPasswords, setRecoveringPasswords] = useState<Record<string, boolean>>({});
  // pinnedPasswords stores passwords that were just reset — takes priority over stale server data
  const [pinnedPasswords, setPinnedPasswords] = useState<Record<string, string>>({});
  const users = useMemo(() => masterUsers.filter((u:any) => u.memberships.some((m:any) => String(m.tenantId) === String(tenantId))), [masterUsers, tenantId]);

  const revealOrRecoverPassword = (user: any) => {
    const email = String(user.email || '').toLowerCase();
    if (!email) return;
    setVisiblePasswords(prev => ({ ...prev, [email]: !prev[email] }));
  };

  const resetAndRevealPassword = async (user: any) => {
    const email = String(user.email || '').toLowerCase();
    if (!email) return;

    if (!window.confirm(`Are you sure you want to reset/change the password for ${email}?`)) {
      return;
    }

    setRecoveringPasswords(prev => ({ ...prev, [email]: true }));
    try {
      const result = await onResetPassword(email);
      // Pin the new password locally so server refetches can't overwrite what's displayed
      if (result?.temporaryPassword) {
        setPinnedPasswords(prev => ({ ...prev, [email]: result.temporaryPassword }));
      }
      setVisiblePasswords(prev => ({ ...prev, [email]: true }));
    } catch {
      // The shared mutation already reports the failure to the user.
    } finally {
      setRecoveringPasswords(prev => ({ ...prev, [email]: false }));
    }
  };
  
  if (users.length === 0) return <tr><td colSpan={4} className="py-10 text-center text-slate-300 text-[10px] font-black uppercase tracking-[0.2em]">Zero identities configured</td></tr>;

  return (
    <>
      {users.map((user:any) => {
        const membership = user.memberships.find((m:any) => String(m.tenantId) === String(tenantId));
        const isAdmin = membership?.role === 'super_admin' || membership?.role === 'ADMIN' || user.email.includes("admin");
        const isRecovering = Boolean(recoveringPasswords[user.email]);
        return (
          <tr key={user.email} className="group hover:bg-slate-50/10 transition-all border-b border-slate-100">
            <td className="px-3 sm:px-6 py-3 sm:py-4">
              <div className="flex items-center gap-2">
                <div className="flex flex-col gap-1">
                  <div className="font-mono font-bold text-slate-900 text-[12px] sm:text-[13px] tracking-tight">
                    {user.username || user.email.split('@')[0]}
                  </div>
                  {/* Role badge — visible on mobile only, hidden on sm+ where the ROLE column shows */}
                  <span className={`sm:hidden self-start text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isAdmin ? 'bg-[#f3e8ff] text-[#9333ea]' : 'bg-[#dcfce7] text-[#16a34a]'}`}>
                    {isAdmin ? 'ADMIN' : 'OPERATOR'}
                  </span>
                  {(user.username && user.username !== user.email.split('@')[0]) && (
                    <div className="text-[9px] text-slate-400 font-medium hidden sm:block">EMAIL: {user.email}</div>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:flex"
                  onClick={() => {
                    navigator.clipboard.writeText(user.username || user.email.split('@')[0]);
                    toast({ title: "Copied!", description: "Username copied to clipboard" });
                  }}
                >
                  <Copy className="h-3 w-3 text-slate-400" />
                </Button>
              </div>
            </td>
            <td className="px-3 sm:px-6 py-3 sm:py-4 hidden sm:table-cell">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${isAdmin ? 'bg-[#f3e8ff] text-[#9333ea]' : 'bg-[#dcfce7] text-[#16a34a]'}`}>
                {isAdmin ? "ADMIN" : "OPERATOR"}
              </span>
            </td>
            <td className="px-3 sm:px-6 py-3 sm:py-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <span className={`text-slate-900 ${visiblePasswords[user.email] ? 'font-mono' : 'font-bold tracking-[0.2em]'} text-xs sm:text-sm`}>
                  {isRecovering ? 'GEN...' : visiblePasswords[user.email] ? (pinnedPasswords[user.email.toLowerCase()] || user.plainPassword || '[ENCRYPTED]') : '••••••••'}
                </span>
                <div className="flex items-center gap-1.5 sm:gap-2 opacity-50 hover:opacity-100 transition-opacity text-slate-400">
                  <button 
                    onClick={() => revealOrRecoverPassword(user)} 
                    className="hover:text-slate-600 focus:outline-none"
                    title={user.plainPassword ? "Toggle Visibility" : "Reveal Password"}
                    disabled={isRecovering}
                  >
                    {isRecovering ? <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" /> : visiblePasswords[user.email] ? <EyeOff className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                  </button>
                  <button 
                    onClick={() => {
                      const passwordToCopy = pinnedPasswords[user.email.toLowerCase()] || user.plainPassword || "";
                      if (passwordToCopy) {
                        navigator.clipboard.writeText(passwordToCopy);
                        toast({ title: "Copied!", description: "Password copied to clipboard" });
                      } else {
                        toast({ title: "Error", description: "Password is not loaded yet.", variant: "destructive" });
                      }
                    }} 
                    className="hover:text-slate-600"
                    title="Copy Password"
                    disabled={isRecovering}
                  >
                    <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                  <button onClick={() => resetAndRevealPassword(user)} className="hover:text-slate-600" disabled={isRecovering}>
                    <RefreshCcw className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${isRecovering ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
            </td>
            <td className="px-3 sm:px-6 py-3 sm:py-4 text-right">
              <button 
                className="text-[11px] font-black uppercase text-red-500 hover:text-red-700 tracking-wider transition-colors"
                onClick={() => {
                  if (window.confirm(`WARNING: Are you sure you want to revoke access and delete the user identity for ${user.email}? This action is permanent.`)) {
                    onRevoke(user.email);
                  }
                }}
              >
                REVOKE
              </button>
            </td>
          </tr>
        );
      })}
    </>
  );
}

// ==============================================================
// PRINT TEMPLATE BUILDER SUB-COMPONENT WITH REAL-TIME PREVIEW
// ==============================================================

const BOILERPLATE_PRESETS = [
  {
    templateId: "indianoil_thermal",
    name: "IndianOil Standard Memo",
    type: "bill" as const,
    logoType: "indianoil" as const,
    styles: `
.receipt-body { font-family: 'Courier New', Courier, monospace; width: 80mm; padding: 4px; font-size: 12px; color: #000; line-height: 1.4; margin: 0 auto; background: #fff; }
.receipt-header { text-align: center; margin-bottom: 8px; }
.receipt-logo { width: 50px; height: 50px; margin: 0 auto 4px auto; display: block; }
.receipt-title { font-size: 15px; font-weight: bold; margin-bottom: 2px; }
.divider { border-top: 1px dashed #000; margin: 6px 0; }
.double-divider { border-top: 2px double #000; margin: 6px 0; }
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; }
.text-right { text-align: right; }
.text-center { text-align: center; }
.bold { font-weight: bold; }
.items-header { font-weight: bold; border-bottom: 1px dashed #000; padding-bottom: 2px; margin-bottom: 4px; }
.thankyou { text-align: center; font-weight: bold; margin-top: 12px; }
    `.trim(),
    htmlStructure: `
<div class="receipt-body">
    <div class="receipt-header">
        <img class="receipt-logo" src="{{logoUrl}}" alt="Logo" />
        <div class="receipt-title">{{orgName}}</div>
        <div>-</div>
        <div>-</div>
        <div>GSTIN: {{orgGst}}</div>
        <div>Mob: {{orgContact}}</div>
        <div class="bold">CASH / CREDIT MEMO</div>
    </div>
    <div class="divider"></div>
    <div class="grid-2">
        <div>MEMO #{{billNo}}</div>
        <div class="text-right">DT: {{date}}</div>
    </div>
    <div class="double-divider"></div>
    <div class="grid-2 items-header">
        <div>ITEM | QUANTITY</div>
        <div class="text-right">| AMOUNT</div>
    </div>
    <div class="grid-2">
        <div>
            <span class="bold">{{product}}</span><br/>
            {{qty}} Ltrs @ {{price}}
        </div>
        <div class="text-right bold" style="align-self: center;">
            {{total}}
        </div>
    </div>
    <div class="divider"></div>
    <div class="grid-2">
        <div class="bold">SUB TOTAL</div>
        <div class="text-right bold">{{total}}</div>
    </div>
    <div class="grid-2">
        <div class="bold">SALE AMOUNT</div>
        <div class="text-right bold" style="font-size: 14px;">₹{{total}}</div>
    </div>
    <div class="divider"></div>
    <div class="grid-2" style="font-size: 11px;">
        <div>GST (CGST+SGST)</div>
        <div class="text-right">SALE AMOUNT</div>
    </div>
    <div class="grid-2" style="font-size: 11px;">
        <div>₹{{cgst}} | ₹{{sgst}}</div>
        <div class="text-right">₹{{total}}</div>
    </div>
    <div class="double-divider"></div>
    <div class="thankyou">THANK YOU VISIT AGAIN!</div>
</div>
    `.trim()
  },
  {
    templateId: "clean_metadata_receipt",
    name: "Clean Metadata Slip",
    type: "bill" as const,
    logoType: "indianoil" as const,
    styles: `
.receipt-body { font-family: 'Courier New', Courier, monospace; width: 80mm; padding: 4px; font-size: 12px; color: #000; line-height: 1.4; margin: 0 auto; background: #fff; }
.receipt-header { text-align: center; margin-bottom: 8px; }
.receipt-logo { width: 50px; height: 50px; margin: 0 auto 4px auto; display: block; }
.receipt-title { font-size: 15px; font-weight: bold; margin-bottom: 2px; }
.divider { border-top: 1px dashed #000; margin: 6px 0; }
.grid-row { display: flex; justify-content: space-between; margin-bottom: 3px; }
.text-right { text-align: right; }
.text-center { text-align: center; }
.bold { font-weight: bold; }
.thankyou { text-align: center; font-weight: bold; margin-top: 12px; }
    `.trim(),
    htmlStructure: `
<div class="receipt-body">
    <div class="receipt-header">
        <img class="receipt-logo" src="{{logoUrl}}" alt="Logo" />
        <div class="receipt-title">{{orgName}}</div>
        <div>-</div>
        <div>-</div>
        <div>GSTIN: {{orgGst}}</div>
        <div>Mob: {{orgContact}}</div>
        <div class="bold">CASH / CREDIT MEMO</div>
    </div>
    <div class="divider"></div>
    <div class="grid-row">
        <span>Txn Date</span>
        <span class="bold">{{date}}</span>
    </div>
    <div class="grid-row">
        <span>Txn Time</span>
        <span class="bold">{{time}}</span>
    </div>
    <div class="grid-row">
        <span>Txn ID</span>
        <span class="bold">#{{billNo}}</span>
    </div>
    <div class="grid-row">
        <span>Txn Type</span>
        <span class="bold">Credit</span>
    </div>
    <div class="grid-row">
        <span>Product</span>
        <span class="bold">{{product}}</span>
    </div>
    <div class="grid-row">
        <span>Price</span>
        <span class="bold">{{price}}</span>
    </div>
    <div class="grid-row">
        <span>Qty</span>
        <span class="bold">{{qty}}</span>
    </div>
    <div class="grid-row">
        <span>Discount (-)</span>
        <span class="bold">{{discount}}</span>
    </div>
    <div class="grid-row" style="font-size: 13px;">
        <span class="bold">Amount (₹)</span>
        <span class="bold">₹{{total}}</span>
    </div>
    <div class="grid-row">
        <span>Mobile NO</span>
        <span class="bold">{{mobile}}</span>
    </div>
    <div class="divider"></div>
    <div class="thankyou">
        <div>Thank You</div>
        <div style="font-size: 14px; margin-top: 4px;">Please Visit Again</div>
    </div>
</div>
    `.trim()
  },
  {
    templateId: "hp_cash_bill",
    name: "HP Cash Bill Standard",
    type: "bill" as const,
    logoType: "hp" as const,
    styles: `
.hpbill { font-family: 'Courier New', Courier, monospace; width: 80mm; padding: 4px 6px; font-size: 11px; color: #000; line-height: 1.45; margin: 0 auto; background: #fff; }
.hpbill-top-gstin { font-size: 10px; font-weight: bold; margin-bottom: 4px; }
.hpbill-header { display: flex; align-items: flex-start; gap: 6px; margin-bottom: 6px; }
.hpbill-logo { width: 44px; height: 44px; object-fit: contain; flex-shrink: 0; }
.hpbill-center { flex: 1; text-align: center; }
.hpbill-title { font-size: 13px; font-weight: bold; text-transform: uppercase; }
.hpbill-sub { font-size: 10px; }
.hpbill-heading { text-align: center; font-weight: bold; font-size: 12px; margin: 4px 0; border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 2px 0; }
.hpbill-divider { border-top: 1px dashed #000; margin: 4px 0; }
.hpbill-row { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 2px; }
.hpbill-table { width: 100%; border-collapse: collapse; margin: 4px 0; font-size: 11px; }
.hpbill-table th { border: 1px solid #000; padding: 3px 4px; font-weight: bold; text-align: left; }
.hpbill-table th:not(:first-child) { text-align: right; }
.hpbill-table td { border: 1px solid #000; padding: 3px 4px; text-align: left; vertical-align: top; }
.hpbill-table td:not(:first-child) { text-align: right; }
.hpbill-sale { display: flex; justify-content: space-between; font-weight: bold; font-size: 12px; margin: 4px 0; padding: 3px 2px; }
.hpbill-sign { display: flex; justify-content: space-between; font-size: 10px; color: #1a56db; margin-top: 14px; padding-top: 4px; }
.hpbill-footer { text-align: center; font-weight: bold; font-size: 12px; margin-top: 6px; border: 1px solid #000; padding: 4px 0; letter-spacing: 0.5px; }
    `.trim(),
    htmlStructure: `
<div class="hpbill">
    <div class="hpbill-top-gstin">GSTIN : {{orgGst}}</div>
    <div class="hpbill-header">
        <img class="hpbill-logo" src="{{logoUrl}}" alt="HP Logo" />
        <div class="hpbill-center">
            <div class="hpbill-title">{{orgName}}</div>
            <div class="hpbill-sub">-</div>
            <div class="hpbill-sub">-</div>
            <div class="hpbill-sub">{{orgGst}}</div>
            <div class="hpbill-sub">{{orgContact}}</div>
        </div>
    </div>
    <div class="hpbill-heading">Cash Bill</div>
    <div class="hpbill-divider"></div>
    <div class="hpbill-row"><span>MEMO : #{{billNo}}</span><span>DT : {{date}}</span></div>
    <div class="hpbill-row"><span>Bill :</span><span>Indent : {{indentNo}}</span></div>
    <div class="hpbill-row"><span>Cust : {{customerName}}</span><span>Mobile : {{mobile}}</span></div>
    <div class="hpbill-row"><span>Vehicle : {{vehicleNo}}</span><span>EMP : {{employee}}</span></div>
    <div class="hpbill-divider"></div>
    <table class="hpbill-table">
        <thead>
            <tr>
                <th>ITEM</th>
                <th>QUANTITY</th>
                <th>RATE</th>
                <th>AMOUNT</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td>{{product}}</td>
                <td>{{qty}}</td>
                <td>{{price}}</td>
                <td>{{total}}</td>
            </tr>
        </tbody>
    </table>
    <div class="hpbill-sale">
        <span></span>
        <span>SALE<br/>AMOUNT</span>
        <span style="align-self:flex-end;">{{total}}</span>
    </div>
    <div class="hpbill-sign">
        <span>Customer Sign</span>
        <span>For {{orgName}}</span>
    </div>
    <div class="hpbill-divider"></div>
    <div class="hpbill-footer">THANKYOU VISIT AGAIN!</div>
</div>
    `.trim()
  },

  {
    templateId: "landscape_tax_invoice",
    name: "Landscape A4 Invoice",
    type: "invoice" as const,
    logoType: "indianoil" as const,
    styles: `
.invoice-body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; width: 100%; padding: 15px; font-size: 12px; color: #333; line-height: 1.4; background: #fff; box-sizing: border-box; }
.invoice-header { display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 10px; }
.brand-logos { display: flex; align-items: center; gap: 10px; }
.brand-logo-img { height: 40px; }
.company-info { text-align: center; flex-grow: 1; }
.company-name { font-size: 20px; font-weight: bold; text-transform: uppercase; color: #1e3a8a; }
.invoice-title { font-size: 16px; font-weight: bold; text-align: center; text-transform: uppercase; margin: 8px 0; letter-spacing: 2px; }
.invoice-details { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #333; margin-bottom: 10px; }
.details-box { padding: 8px; border-right: 1px solid #333; }
.details-box:last-child { border-right: none; }
.invoice-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
.invoice-table th, .invoice-table td { border: 1px solid #333; padding: 8px; text-align: left; }
.invoice-table th { background-color: #f3f4f6; font-weight: bold; text-transform: uppercase; }
.total-row { font-weight: bold; background-color: #fafafa; }
.footer-grid { display: grid; grid-template-columns: 1.5fr 1fr; gap: 15px; margin-top: 15px; }
.bank-details { border: 1px solid #333; padding: 8px; font-size: 11px; }
.signature-box { border: 1px solid #333; padding: 8px; text-align: center; height: 80px; display: flex; flex-direction: column; justify-content: space-between; }
    `.trim(),
    htmlStructure: `
<div class="invoice-body">
    <div class="invoice-header">
        <div class="brand-logos">
            <img class="brand-logo-img" src="{{logoUrlLeft}}" alt="Logo Left" style="height: 40px; {{logoLeftStyle}}" />
        </div>
        <div class="company-info">
            <div class="company-name">{{orgName}}</div>
            <div style="margin-top: 5px;">{{orgAddress}}</div>
            <div style="font-weight: bold; margin-top: 3px;">{{orgGst}}</div>
        </div>
        <div class="brand-logos" style="flex-direction: column; align-items: flex-end; gap: 4px; {{logoRightContainerStyle}}">
            <div style="font-size: 11px; font-weight: bold; white-space: nowrap;">MOBILE: {{orgContact}}</div>
            <img class="brand-logo-img" src="{{logoUrlRight}}" alt="Logo Right" style="height: 40px; {{logoRightStyle}}" />
        </div>
    </div>
    <div class="invoice-title">INVOICE</div>
    <div class="invoice-details">
        <div class="details-box">
            <strong style="text-transform: uppercase;">TO.</strong><br/>
            <div style="margin-top: 3px; font-size: 13px; font-weight: bold;">{{customerName}}</div>
            <div style="color: #666; margin-top: 2px;">GSTIN: {{customerGst}}</div>
            <div style="color: #666;">Mob: {{customerMobile}}</div>
        </div>
        <div class="details-box">
            <div style="display: grid; grid-template-columns: 100px 1fr; gap: 3px;">
                <span>Payment Terms</span><strong>: no data</strong>
                <span>Invoice No</span><strong>: #{{billNo}}</strong>
                <span>Date</span><strong>: {{date}}</strong>
            </div>
        </div>
    </div>
    <table class="invoice-table">
        <thead>
            <tr>
                <th style="width: 8%;">S.No</th>
                <th style="width: 42%;">Description</th>
                <th style="width: 12%; text-align: right;">Qty</th>
                <th style="width: 12%; text-align: right;">Rate</th>
                <th style="width: 12%; text-align: right;">D.Amt</th>
                <th style="width: 14%; text-align: right;">Total Amount</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td>1</td>
                <td style="font-weight: bold;">{{product}}</td>
                <td style="text-align: right;">{{qty}}</td>
                <td style="text-align: right;">{{price}}</td>
                <td style="text-align: right;">{{discount}}</td>
                <td style="text-align: right; font-weight: bold;">₹{{total}}</td>
            </tr>
            <tr class="total-row">
                <td colspan="2" style="text-align: right;">Total</td>
                <td style="text-align: right;">{{qty}}</td>
                <td style="text-align: right;">{{price}}</td>
                <td style="text-align: right;">{{discount}}</td>
                <td style="text-align: right;">₹{{total}}</td>
            </tr>
        </tbody>
    </table>
    <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #333; padding-bottom: 6px;">
        <span>E. & O.E.</span>
        <div>Net Amount: <strong style="font-size: 14px; color: #1e3a8a;">₹{{total}}</strong></div>
    </div>
    <div class="footer-grid">
        <div class="bank-details">
            <strong style="text-transform: uppercase; color: #1e3a8a;">Account Details:</strong>
            <div style="margin-top: 3px; display: grid; grid-template-columns: 90px 1fr; gap: 2px;">
                <span>Account No</span><strong>: {{bankAcc}}</strong>
                <span>Bank Name</span><strong>: {{bankName}}</strong>
                <span>Branch Name</span><strong>: {{bankBranch}}</strong>
                <span>IFSC CODE</span><strong>: {{bankIfsc}}</strong>
            </div>
        </div>
        <div class="signature-box">
            <div style="font-weight: bold;">For {{orgName}}</div>
            <div style="font-style: italic; color: #666; font-size: 10px;">Authorised Signatory</div>
        </div>
    </div>
</div>
    `.trim()
  },
  {
    templateId: "portrait_gst_invoice",
    name: "Portrait GST Tax Invoice",
    type: "invoice" as const,
    logoType: "custom" as const,
    styles: `
.invoice-body { font-family: Arial, sans-serif; width: 100%; padding: 10px; font-size: 11px; color: #333; line-height: 1.4; background: #fff; box-sizing: border-box; }
.header-banner { border: 1px solid #000; padding: 8px; display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.brand-logo-img { height: 35px; }
.bunk-title { font-size: 16px; font-weight: bold; text-align: center; color: #1e3a8a; }
.meta-grid { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #000; margin-bottom: 8px; }
.meta-column { padding: 6px; border-right: 1px solid #000; }
.meta-column:last-child { border-right: none; }
.grid-label { color: #555; font-weight: bold; text-transform: uppercase; font-size: 9px; margin-bottom: 2px; }
.invoice-table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
.invoice-table th, .invoice-table td { border: 1px solid #000; padding: 4px 6px; text-align: left; font-size: 10px; }
.invoice-table th { background-color: #f5f5f5; font-weight: bold; text-align: center; }
.totals-container { border: 1px solid #000; display: grid; grid-template-columns: 2fr 1fr; margin-bottom: 8px; }
.totals-left { padding: 6px; border-right: 1px solid #000; }
.totals-right { padding: 6px; display: grid; grid-template-columns: 1fr 1fr; gap: 2px; }
.sign-section { display: grid; grid-template-columns: 1.5fr 1fr; gap: 10px; margin-top: 10px; }
.bank-area { border: 1px solid #000; padding: 6px; }
.sign-area { border: 1px solid #000; padding: 6px; height: 70px; display: flex; flex-direction: column; justify-content: space-between; text-align: center; }
    `.trim(),
    htmlStructure: `
<div class="invoice-body">
    <div style="font-size: 14px; font-weight: bold; text-align: center; letter-spacing: 2px; margin-bottom: 6px;">INVOICE</div>
    <div class="header-banner" style="{{headerBannerStyle}}">
        <img class="brand-logo-img" src="{{logoUrlLeft}}" alt="Logo Left" style="height: 35px; {{logoLeftStyle}}" />
        <img class="brand-logo-img" src="{{logoUrlRight}}" alt="Logo Right" style="height: 35px; {{logoRightStyle}}" />
    </div>
    <div class="meta-grid" style="grid-template-columns: 1.2fr 0.8fr;">
        <div class="meta-column" style="font-size: 10px;">
            <span>Invoice No : <strong>#{{billNo}}</strong></span>
        </div>
        <div class="meta-column" style="font-size: 10px; text-align: right;">
            <span>Dated : <strong>{{date}}</strong></span>
        </div>
    </div>
    <div class="meta-grid">
        <div class="meta-column">
            <div class="grid-label">SELLER :</div>
            <strong style="font-size: 11px; color: #1e3a8a; text-transform: uppercase;">{{orgName}}</strong><br/>
            <div style="margin-top: 3px;">{{orgAddress}}</div>
            <div>{{orgGst}}</div>
            <div>{{orgContact}}</div>
        </div>
        <div class="meta-column">
            <div class="grid-label">BUYER :</div>
            <strong style="font-size: 11px; text-transform: uppercase;">{{customerName}}</strong><br/>
            <div style="margin-top: 3px;">Address: {{customerAddress}}</div>
            <div>GSTIN: {{customerGst}}</div>
            <div>Mobile: {{customerMobile}}</div>
        </div>
    </div>
    <table class="invoice-table">
        <thead>
            <tr>
                <th style="width: 5%;">S.No</th>
                <th style="width: 35%;">Description of Goods</th>
                <th style="width: 8%;">Track</th>
                <th style="width: 10%; text-align: right;">Quantity</th>
                <th style="width: 10%; text-align: right;">Rate</th>
                <th style="width: 10%; text-align: right;">Taxable</th>
                <th style="width: 8%; text-align: right;">CGST</th>
                <th style="width: 8%; text-align: right;">SGST</th>
                <th style="width: 6%; text-align: right;">IGST</th>
                <th style="width: 10%; text-align: right;">Total</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td style="text-align: center;">1</td>
                <td style="font-weight: bold;">{{product}}</td>
                <td style="text-align: center;">-</td>
                <td style="text-align: right;">{{qty}}</td>
                <td style="text-align: right;">{{price}}</td>
                <td style="text-align: right;">{{taxableAmt}}</td>
                <td style="text-align: right;">{{cgst}}</td>
                <td style="text-align: right;">{{sgst}}</td>
                <td style="text-align: right;">0.00</td>
                <td style="text-align: right; font-weight: bold;">₹{{total}}</td>
            </tr>
            <tr style="font-weight: bold; background: #fafafa;">
                <td colspan="3" style="text-align: right;">Total</td>
                <td style="text-align: right;">{{qty}}</td>
                <td style="text-align: right;">{{price}}</td>
                <td style="text-align: right;">{{taxableAmt}}</td>
                <td style="text-align: right;">{{cgst}}</td>
                <td style="text-align: right;">{{sgst}}</td>
                <td style="text-align: right;">0.00</td>
                <td style="text-align: right;">₹{{total}}</td>
            </tr>
        </tbody>
    </table>
    <div class="totals-container">
        <div class="totals-left">
            <strong>Amount Chargeable (in words):</strong>
            <div style="font-weight: bold; margin-top: 3px; font-style: italic; color: #1e3a8a;">{{totalInWords}}</div>
        </div>
        <div class="totals-right">
            <span>CGST:</span><strong style="text-align: right;">₹{{cgst}}</strong>
            <span>SGST:</span><strong style="text-align: right;">₹{{sgst}}</strong>
            <span>IGST:</span><strong style="text-align: right;">₹0.00</strong>
            <span style="font-size: 11px; font-weight: bold;">Total:</span><strong style="text-align: right; font-size: 11px; color: #1e3a8a;">₹{{total}}</strong>
        </div>
    </div>
    <div style="border: 1px solid #000; padding: 6px; font-size: 10px;">
        <strong>Declaration:</strong><br/>
        We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.
    </div>
    <div class="sign-section">
        <div class="bank-area">
            <strong style="color: #1e3a8a;">Bank Details:</strong>
            <div style="margin-top: 3px; display: grid; grid-template-columns: 80px 1fr; gap: 2px; font-size: 10px;">
                <span>Account No</span><strong>: {{bankAcc}}</strong>
                <span>Bank Name</span><strong>: {{bankName}}</strong>
                <span>Branch Name</span><strong>: {{bankBranch}}</strong>
                <span>IFSC CODE</span><strong>: {{bankIfsc}}</strong>
            </div>
        </div>
        <div class="sign-area">
            <div style="font-weight: bold; font-size: 10px;">For {{orgName}}</div>
            <div style="font-size: 9px; color: #666;">Authorised Signatory</div>
        </div>
    </div>
    <div style="text-align: center; margin-top: 10px; font-size: 9px; color: #777;">This is a Computer Generated Invoice</div>
</div>
    `.trim()
  }
];

const MOCK_PREVIEW_DATA = {
  orgName: "GALAXY FUELS RETAIL",
  orgGst: "37ABCDE1234F1Z9",
  orgContact: "+91 9988776655",
  billNo: "CS-2026-1002",
  date: "2026-05-26",
  time: "18:05:12",
  product: "HIGH SPEED DIESEL",
  qty: "52.40",
  price: "96.50",
  total: "5056.60",
  cgst: "385.50",
  sgst: "385.50",
  discount: "0.00",
  mobile: "9900112233",
  indentNo: "IND-9092",
  customerName: "GALAXY TOURS & TRAVELS",
  vehicleNo: "AP 03 TX 7788",
  employee: "Dinesh Kumar",
  customerGst: "37GHIJK5678L2Z5",
  customerMobile: "9988998899",
  customerAddress: "Main Road, Tirupati, AP",
  taxableAmt: "4285.60",
  totalInWords: "Five Thousand Fifty Six Rupees and Sixty Paise Only",
  bankAcc: "998877665544321",
  bankName: "HDFC Bank Ltd",
  bankIfsc: "HDFC0000123"
};

interface PrintTemplateBuilderProps {
  tenants: any[];
  toast: any;
}

function PrintTemplateBuilder({ tenants, toast }: PrintTemplateBuilderProps) {
  const [selectedTenantId, setSelectedTenantIdState] = useState<string>(() => {
    const match = document.cookie.match(/(?:^|; )developer_tenant_id=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : "";
  });

  const setSelectedTenantId = useCallback((val: string) => {
    setSelectedTenantIdState(val);
    if (val === "default" || !val) {
      document.cookie = "developer_tenant_id=; path=/; max-age=0; SameSite=Lax";
    } else {
      document.cookie = `developer_tenant_id=${encodeURIComponent(val)}; path=/; max-age=31536000; SameSite=Lax`;
    }
  }, []);

  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editor states
  const [editTemplateId, setEditTemplateId] = useState("");
  const [editName, setEditName] = useState("");
  const [editType, setEditType] = useState<"bill" | "invoice">("bill");
  const [resolvedTenantInfo, setResolvedTenantInfo] = useState<any>(null);

  useEffect(() => {
    const fetchDebugInfo = async () => {
      try {
        const res = await fetch("/api/print-templates/debug-tenant");
        const json = await res.json();
        if (json.success) {
          setResolvedTenantInfo(json);
        }
      } catch (err) {
        console.error("Failed to fetch debug info:", err);
      }
    };
    fetchDebugInfo();
  }, [selectedTenantId]);
  const [editLogoType, setEditLogoType] = useState<string>("custom");
  const [editHtml, setEditHtml] = useState("");
  const [editStyles, setEditStyles] = useState("");

  const fetchTemplatesForTenant = useCallback(async (tenantId: string) => {
    try {
      setLoading(true);
      const res = await fetch(`/api/print-templates?tenantId=${encodeURIComponent(tenantId)}`, {
        credentials: "include"
      });
      const json = await res.json();
      if (json.success) {
        setTemplates(json.rows || []);
        // Auto load first template in list into editor
        if (json.rows && json.rows.length > 0) {
          loadTemplateIntoEditor(json.rows[0]);
        } else {
          clearEditor();
        }
      } else {
        toast({ title: "Failed", description: "Could not load templates for tenant", variant: "destructive" });
      }
    } catch (err) {
      console.error(err);
      toast({ title: "Error", description: "Network error loading print templates", variant: "destructive" });
    } finally {
      setLoading(false);
    }
   
  }, [toast]);

  useEffect(() => {
    if (tenants.length > 0 && !selectedTenantId) {
      const cookieMatch = document.cookie.match(/(?:^|; )developer_tenant_id=([^;]*)/);
      if (!cookieMatch) {
        setSelectedTenantId(tenants[0].id);
      } else {
        setSelectedTenantIdState(decodeURIComponent(cookieMatch[1]));
      }
    }
  }, [tenants, selectedTenantId, setSelectedTenantId]);

  useEffect(() => {
    // Immediately wipe stale data from previous tenant so the
    // directory never shows the wrong bunk's templates
    setTemplates([]);
    setEditTemplateId("custom_template_" + Date.now().toString().slice(-4));
    setEditName("New Custom Print Layout");
    setEditType("bill");
    setEditLogoType("custom");
    setEditHtml("");
    setEditStyles("");

    if (selectedTenantId) {
      fetchTemplatesForTenant(selectedTenantId);
    }
   
  }, [selectedTenantId, fetchTemplatesForTenant]);


  const loadTemplateIntoEditor = (t: any) => {
    setEditTemplateId(t.templateId);
    setEditName(t.name);
    setEditType(t.type);
    setEditLogoType(t.logoType || "custom");
    setEditHtml(t.htmlStructure);
    setEditStyles(t.styles || "");
  };

  const clearEditor = () => {
    setEditTemplateId("custom_template_" + Date.now().toString().slice(-4));
    setEditName("New Custom Print Layout");
    setEditType("bill");
    setEditLogoType("custom");
    setEditHtml("<div>\n  <h3>{{orgName}}</h3>\n  <p>Receipt Memo: {{billNo}}</p>\n  <p>Amount: ₹{{total}}</p>\n</div>");
    setEditStyles(".bold { font-weight: bold; }");
  };

  const handleLoadBoilerplate = (preset: typeof BOILERPLATE_PRESETS[0]) => {
    setEditTemplateId(preset.templateId);
    setEditName(preset.name);
    setEditType(preset.type);
    setEditLogoType(preset.logoType);
    setEditHtml(preset.htmlStructure);
    setEditStyles(preset.styles);
    toast({ title: "Preset Loaded", description: `Loaded structure for "${preset.name}".` });
  };

  const handleSaveTemplate = async () => {
    if (!selectedTenantId) {
      toast({ title: "Save Aborted", description: "Please select a tenant bunk first.", variant: "destructive" });
      return;
    }
    if (!editTemplateId || !editName || !editHtml) {
      toast({ title: "Incomplete Form", description: "Template ID, Display Name, and HTML are required.", variant: "destructive" });
      return;
    }
    try {
      setSaving(true);
      const res = await fetch("/api/print-templates/developer/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          templateId: editTemplateId,
          name: editName,
          type: editType,
          logoType: editLogoType,
          htmlStructure: editHtml,
          styles: editStyles
        }),
        credentials: "include"
      });
      const json = await res.json();
      if (json.success) {
        toast({ title: "Success", description: "Print template successfully written/updated in tenant container." });
        
        // Refresh templates list
        fetchTemplatesForTenant(selectedTenantId);
      } else {
        toast({ title: "Failed", description: json.error || "Failed to save template", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to save print template", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const STANDARD_IDS = ["indianoil_thermal", "clean_metadata_receipt", "hp_cash_bill", "landscape_tax_invoice", "portrait_gst_invoice"];

  const deleteTemplateById = async (templateId: string, templateName: string) => {
    if (!selectedTenantId) {
      toast({ title: "Delete Aborted", description: "Please select a tenant bunk first.", variant: "destructive" });
      return;
    }
    if (STANDARD_IDS.includes(templateId)) {
      toast({ title: "Operation Blocked", description: "Standard default templates cannot be deleted.", variant: "destructive" });
      return;
    }
    if (!window.confirm(`Are you sure you want to permanently delete the custom template "${templateName}"?`)) {
      return;
    }
    try {
      setSaving(true);
      const res = await fetch(`/api/print-templates/developer/${templateId}?tenantId=${encodeURIComponent(selectedTenantId)}`, {
        method: "DELETE",
        credentials: "include"
      });
      const json = await res.json();
      if (json.success) {
        toast({ title: "Deleted", description: `"${templateName}" removed successfully.` });
        // If the deleted template was open in editor, clear it
        if (editTemplateId === templateId) clearEditor();
        fetchTemplatesForTenant(selectedTenantId);
      } else {
        toast({ title: "Failed", description: json.error || "Failed to delete template", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to delete template", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = () => deleteTemplateById(editTemplateId, editName);

  // Compile sandboxed real-time review HTML
  const getCompiledPreview = () => {
    let html = editHtml;

    // Resolve logoUrl dynamically based on the active editLogoType state
    const logoUrl = {
      indianoil: "/logo-indianoil.svg",
      hp: "/logo-hp.svg",
      bpcl: "/logo-bpcl.svg",
      reliance: "/logo-reliance.svg",
      custom: "/brand-logo.png"
    }[editLogoType || "custom"] || "/brand-logo.png";

    const isCustomLogo = !editLogoType || editLogoType === "custom";
    const logoUrlLeft = isCustomLogo ? "/logo-indianoil.svg" : logoUrl;
    const logoUrlRight = "";
    const logoLeftStyle = "";
    const logoRightStyle = "display: none;";
    const logoRightContainerStyle = "display: none;";
    const headerBannerStyle = isCustomLogo ? "" : "justify-content: center;";

    const previewData = {
      ...MOCK_PREVIEW_DATA,
      logoUrl,
      logoUrlLeft,
      logoUrlRight,
      logoLeftStyle,
      logoRightStyle,
      logoRightContainerStyle,
      headerBannerStyle
    };

    Object.entries(previewData).forEach(([key, val]) => {
      const placeholder = new RegExp(`{{${key}}}`, "g");
      html = html.replace(placeholder, val);
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body { 
            background: #fff; 
            margin: 0; 
            padding: 15px; 
            color: #000;
            box-sizing: border-box;
          }
          ${editStyles}
        </style>
      </head>
      <body>
        ${html}
      </body>
      </html>
    `;
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 text-slate-800">
      
      {/* Left side editor panel */}
      <div className="xl:col-span-7 space-y-6">
        <Card className="bg-white border-slate-200 shadow-sm rounded-2xl overflow-hidden">
          <CardHeader className="bg-slate-50 border-b border-slate-200 py-4 px-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-base font-bold text-slate-800">🖨️ Dynamic Print Templates Builder</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Configure seeded boilerplates or define custom print modules per tenant.</p>
              </div>

              {/* Tenant context selector */}
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider shrink-0">Tenant Context:</span>
                  <Select value={selectedTenantId || 'default'} onValueChange={v => setSelectedTenantId(v)}>
                    <SelectTrigger className="h-8 w-44 bg-white border-slate-200 text-xs font-semibold">
                      <SelectValue placeholder="Select bunk..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default" className="text-xs font-bold text-indigo-600">👤 Developer Default</SelectItem>
                      {tenants.map(t => (
                        <SelectItem key={t.id} value={t.id} className="text-xs">{t.organizationName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {resolvedTenantInfo && (
                  <div className="text-[9px] text-slate-400 font-mono">
                    Active DB Context: <span className="text-indigo-600 font-bold">{resolvedTenantInfo.resolvedTenantId === "6998cb22ea5b9188736002ec" ? "Developer Default" : resolvedTenantInfo.resolvedTenantId}</span>
                  </div>
                )}
                <div className="flex gap-2 mt-1">
                  <button
                    className="text-[9px] font-bold border border-red-200 text-red-600 hover:bg-red-50 px-2 py-0.5 rounded transition-all bg-white shadow-sm flex items-center gap-1"
                    onClick={async () => {
                      if (!window.confirm("This will permanently delete all custom print templates saved under the Developer Default account. Are you sure?")) return;
                      try {
                        const res = await fetch("/api/print-templates/purge-orphans?fromTenantId=6998cb22ea5b9188736002ec", { method: 'DELETE' });
                        const json = await res.json();
                        if (json.success) {
                          toast({ title: "Purged", description: `Successfully deleted ${json.deletedCount || 0} orphaned templates.` });
                          if (selectedTenantId) fetchTemplatesForTenant(selectedTenantId);
                        } else {
                          toast({ title: "Failed", description: json.error || "Purge failed", variant: "destructive" });
                        }
                      } catch (err: any) {
                        toast({ title: "Error", description: err.message, variant: "destructive" });
                      }
                    }}
                  >
                    🧹 Purge Dev Orphans
                  </button>
                  
                  {selectedTenantId && selectedTenantId !== "default" && (
                    <button
                      className="text-[9px] font-bold border border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-2 py-0.5 rounded transition-all bg-white shadow-sm flex items-center gap-1"
                      onClick={async () => {
                        if (!window.confirm("This will move any custom print templates saved under the Developer Default account to this Bunk. Proceed?")) return;
                        try {
                          const res = await fetch(`/api/print-templates/purge-orphans?fromTenantId=6998cb22ea5b9188736002ec&toTenantId=${encodeURIComponent(selectedTenantId)}`, { method: 'DELETE' });
                          const json = await res.json();
                          if (json.success) {
                            toast({ title: "Migrated", description: `Successfully migrated ${json.moved || 0} templates to this bunk.` });
                            fetchTemplatesForTenant(selectedTenantId);
                          } else {
                            toast({ title: "Failed", description: json.error || "Migration failed", variant: "destructive" });
                          }
                        } catch (err: any) {
                          toast({ title: "Error", description: err.message, variant: "destructive" });
                        }
                      }}
                    >
                      🚀 Migrate Dev Orphans
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-4">
            
            {/* Template selector and quick Actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Templates list:</span>
                <Select 
                  value={templates.some(t => t.templateId === editTemplateId) ? editTemplateId : ""}
                  onValueChange={v => {
                    const matched = templates.find(t => t.templateId === v);
                    if (matched) loadTemplateIntoEditor(matched);
                  }}
                >
                  <SelectTrigger className="h-8 w-56 bg-white border-slate-200 text-xs font-medium">
                    <SelectValue placeholder="Create/Select template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.templateId} value={t.templateId} className="text-xs">
                        {t.name} ({t.type === "bill" ? "80mm" : "A4"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={clearEditor} 
                  className="h-8 text-xs font-semibold border-slate-200 text-slate-600 hover:bg-slate-100 flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New Custom
                </Button>
                
                <Button 
                  size="sm" 
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  className="h-8 bg-slate-900 hover:bg-black text-white text-xs font-bold px-4 flex items-center gap-1.5"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save Template Structure
                </Button>

                {!["indianoil_thermal", "clean_metadata_receipt", "hp_cash_bill", "landscape_tax_invoice", "portrait_gst_invoice"].includes(editTemplateId) && editTemplateId && (
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={handleDeleteTemplate}
                    disabled={saving}
                    className="h-8 bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-4 flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete Layout
                  </Button>
                )}
              </div>
            </div>

            {/* Bunk Templates Directory */}
            {templates.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">📁 Bunk Templates Directory</Label>
                  <span className="text-[10px] text-slate-400">{templates.length} template{templates.length !== 1 ? 's' : ''} saved</span>
                </div>
                <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px] w-4">●</th>
                        <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Name</th>
                        <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px] hidden sm:table-cell">ID</th>
                        <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Type</th>
                        <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Kind</th>
                        <th className="text-right px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {templates.map((t: any) => {
                        const isStandard = STANDARD_IDS.includes(t.templateId);
                        const isLoaded = t.templateId === editTemplateId;
                        return (
                          <tr
                            key={t.templateId}
                            className={`transition-colors ${
                              isLoaded ? 'bg-blue-50' : 'hover:bg-slate-50'
                            }`}
                          >
                            {/* Active status dot */}
                            <td className="px-3 py-2">
                              <div className={`w-2 h-2 rounded-full ${
                                t.isActive ? 'bg-green-500' : 'bg-slate-300'
                              }`} title={t.isActive ? 'Active' : 'Inactive'} />
                            </td>
                            {/* Name */}
                            <td className="px-3 py-2">
                              <span className={`font-semibold text-slate-800 ${isLoaded ? 'text-blue-700' : ''}`}>{t.name}</span>
                              {t.isActive && (
                                <span className="ml-1.5 text-[9px] bg-green-100 text-green-700 font-bold px-1 py-0.5 rounded uppercase">Active</span>
                              )}
                            </td>
                            {/* ID */}
                            <td className="px-3 py-2 hidden sm:table-cell">
                              <code className="text-[10px] text-slate-500 font-mono bg-slate-100 px-1.5 py-0.5 rounded">{t.templateId}</code>
                            </td>
                            {/* Type */}
                            <td className="px-3 py-2">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${
                                t.type === 'bill'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-purple-100 text-purple-700'
                              }`}>
                                {t.type === 'bill' ? '80mm' : 'A4'}
                              </span>
                            </td>
                            {/* Kind badge */}
                            <td className="px-3 py-2">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${
                                isStandard
                                  ? 'bg-slate-100 text-slate-500'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {isStandard ? 'Standard' : 'Custom'}
                              </span>
                            </td>
                            {/* Actions */}
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  onClick={() => loadTemplateIntoEditor(t)}
                                  className="text-[10px] font-bold text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                  title="Load this template into the editor"
                                >
                                  Edit
                                </button>
                                {!isStandard && (
                                  <button
                                    onClick={() => deleteTemplateById(t.templateId, t.name)}
                                    disabled={saving}
                                    className="text-[10px] font-bold text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors disabled:opacity-40"
                                    title="Permanently delete this custom template"
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Presets Boilerplate quick load */}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold text-slate-500 uppercase">⚡ Quick Load Boilerplate Presets</Label>
              <div className="flex flex-wrap gap-2">
                {BOILERPLATE_PRESETS.map(preset => (
                  <Button 
                    key={preset.templateId}
                    size="sm"
                    variant="outline"
                    type="button"
                    onClick={() => handleLoadBoilerplate(preset)}
                    className="h-7 text-[10px] font-medium border-slate-200 hover:bg-indigo-50/50 hover:text-indigo-600 hover:border-indigo-200"
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Form Fields Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-500 uppercase">Template Unique ID</Label>
                <Input 
                  className="h-9 text-xs font-semibold focus:bg-slate-50/50" 
                  value={editTemplateId} 
                  onChange={e => setEditTemplateId(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                  placeholder="e.g. customized_invoice"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-500 uppercase">Display Name</Label>
                <Input 
                  className="h-9 text-xs focus:bg-slate-50/50" 
                  value={editName} 
                  onChange={e => setEditName(e.target.value)} 
                  placeholder="e.g. IndianOil Thermal Slip"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold text-slate-500 uppercase">Logo Style</Label>
                  <Select value={editLogoType} onValueChange={v => setEditLogoType(v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Logo..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="indianoil" className="text-xs">IndianOil</SelectItem>
                      <SelectItem value="hp" className="text-xs">HP Logo</SelectItem>
                      <SelectItem value="bpcl" className="text-xs">Bharat Petroleum</SelectItem>
                      <SelectItem value="reliance" className="text-xs">Reliance</SelectItem>
                      <SelectItem value="custom" className="text-xs">Custom Logo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold text-slate-500 uppercase">Layout Type</Label>
                  <Select value={editType} onValueChange={(v: "bill" | "invoice") => setEditType(v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Type..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bill" className="text-xs">Bill (80mm)</SelectItem>
                      <SelectItem value="invoice" className="text-xs">Invoice (A4)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Side-by-side structures editing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <Label className="text-[10px] font-bold text-slate-500 uppercase">HTML Layout Structure</Label>
                  <span className="text-[9px] text-slate-400 font-bold font-mono">{"{{placeholders}}"} allowed</span>
                </div>
                <textarea 
                  className="w-full h-96 bg-slate-900 text-emerald-400 p-4 font-mono text-[11px] leading-relaxed rounded-xl focus:outline-none focus:ring-1 focus:ring-slate-700 border-none shadow-inner resize-y"
                  value={editHtml}
                  onChange={e => setEditHtml(e.target.value)}
                  placeholder="<div>\n  <h3>{{orgName}}</h3>\n  ...\n</div>"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-500 uppercase">Custom CSS Print Styles</Label>
                <textarea 
                  className="w-full h-96 bg-slate-900 text-teal-300 p-4 font-mono text-[11px] leading-relaxed rounded-xl focus:outline-none focus:ring-1 focus:ring-slate-700 border-none shadow-inner resize-y"
                  value={editStyles}
                  onChange={e => setEditStyles(e.target.value)}
                  placeholder=".receipt-body {\n  width: 80mm;\n  font-size: 12px;\n}"
                />
              </div>
            </div>

          </CardContent>
        </Card>
      </div>

      {/* Right side live rendering panel */}
      <div className="xl:col-span-5 space-y-6">
        <Card className="bg-white border-slate-200 shadow-sm rounded-2xl overflow-hidden h-full flex flex-col">
          <CardHeader className="bg-slate-50 border-b border-slate-200 py-4 px-6 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base font-bold text-slate-800">📺 Sandbox Live Render View</CardTitle>
              <p className="text-xs text-slate-500 mt-1">Rendered with realistic mock metadata in real-time.</p>
            </div>
            <Badge className="bg-slate-900 text-slate-300 border-slate-800 text-[10px] px-2 py-0.5">Style Isolated</Badge>
          </CardHeader>
          
          <CardContent className="p-6 flex-1 flex flex-col justify-between bg-slate-100/50 min-h-[500px]">
            {/* Sandboxed rendering Frame */}
            <div className="flex-1 rounded-xl overflow-hidden border border-slate-200/80 bg-white shadow-sm flex flex-col">
              <iframe 
                srcDoc={getCompiledPreview()}
                className="w-full flex-1 border-0"
                title="Live Sandboxed Preview"
              />
            </div>

            {/* Dynamic tags checklist guide */}
            <div className="mt-4 bg-slate-900 text-slate-400 p-4 rounded-xl text-xs space-y-1.5 font-mono leading-relaxed border border-slate-800">
              <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block mb-1">📋 Supported Dynamic tags checklist:</span>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                <span><strong className="text-white">{"{{orgName}}"}</strong> - Bunk Name</span>
                <span><strong className="text-white">{"{{orgGst}}"}</strong> - Bunk GSTIN</span>
                <span><strong className="text-white">{"{{orgContact}}"}</strong> - Bunk Mobile</span>
                <span><strong className="text-white">{"{{billNo}}"}</strong> - Memo / Bill No</span>
                <span><strong className="text-white">{"{{date}}"}</strong> - Date</span>
                <span><strong className="text-white">{"{{time}}"}</strong> - Time</span>
                <span><strong className="text-white">{"{{product}}"}</strong> - Gas/Diesel Name</span>
                <span><strong className="text-white">{"{{qty}}"}</strong> - Quantity (Ltrs)</span>
                <span><strong className="text-white">{"{{price}}"}</strong> - Rate (Per Ltr)</span>
                <span><strong className="text-white">{"{{total}}"}</strong> - Total Amount</span>
                <span><strong className="text-white">{"{{cgst}}"} / {"{{sgst}}"}</strong> - Tax</span>
                <span><strong className="text-white">{"{{customerName}}"}</strong> - Customer</span>
                <span><strong className="text-white">{"{{vehicleNo}}"}</strong> - Vehicle No</span>
                <span><strong className="text-white">{"{{employee}}"}</strong> - Operator Name</span>
                <span><strong className="text-white">{"{{totalInWords}}"}</strong> - Words Amount</span>
                <span><strong className="text-white">{"{{bankAcc}}"}</strong> - Bank Acc No</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}

